import React, { useEffect, useRef, useState, useCallback } from 'react'

/**
 * InactivityOverlay
 * - Self-contained: starts an idle timer on mount (only if isActive is true)
 * - Shows a fullscreen dark overlay with the provided message after `idleSeconds`
 * - Any user interaction (mousemove, mousedown, keydown, touchstart, scroll) resets the timer
 * - Clicking the overlay hides it and restarts the timer
 * - isActive prop controls whether the overlay functionality is enabled
 */
const InactivityOverlay = ({ idleSeconds = 60, message = 'Welcome to Local Chapter', isActive = true }) => {
    const [show, setShow] = useState(false)
    const timerRef = useRef(null)

    const clearIdleTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    const startIdleTimer = useCallback(() => {
        if (!isActive) return
        clearIdleTimer()
        timerRef.current = setTimeout(() => setShow(true), idleSeconds * 1000)
    }, [clearIdleTimer, idleSeconds, isActive])

    const handleUserActivity = useCallback(() => {
        if (show) setShow(false)
        if (isActive) startIdleTimer()
    }, [show, startIdleTimer, isActive])

    useEffect(() => {
        // Hide overlay immediately if isActive becomes false
        if (!isActive && show) {
            setShow(false)
        }

        // Clear timer if isActive becomes false
        if (!isActive) {
            clearIdleTimer()
            return
        }

        // Start timer on mount or when isActive becomes true
        startIdleTimer()
        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll']
        events.forEach(evt => window.addEventListener(evt, handleUserActivity, { passive: true }))
        return () => {
            clearIdleTimer()
            events.forEach(evt => window.removeEventListener(evt, handleUserActivity))
        }
    }, [isActive, show, clearIdleTimer, startIdleTimer, handleUserActivity])

    const dismiss = useCallback(() => {
        setShow(false)
        if (isActive) startIdleTimer()
    }, [startIdleTimer, isActive])

    if (!show || !isActive) return null

    return (
        <div
            role="dialog"
            aria-modal="true"
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-100 d-flex align-items-center justify-content-center"
            style={{ zIndex: 9999, cursor: 'pointer' }}
            onClick={dismiss}
        >
            <div className="text-center text-white user-select-none">
                <h1 className="display-3 fw-bold mb-0">{message}</h1>
            </div>
        </div>
    )
}

export default InactivityOverlay
